import { useState } from 'react'
import cn from 'classnames'
import { Checkbox } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { reportError } from '@mc/next-newrelic'
import { useTrackPen7367Experiment } from 'experiments/pen-7367_v1_quiz_subcat_removal/useTrackPen7367Experiment'
import {
  ButtonsSection,
  InteractiveStep,
} from 'pages/find-my-classes/components/Quiz/components'
import { QuizMachineContext } from 'pages/find-my-classes/components/Quiz/context'
import { useQuizEvents } from 'pages/find-my-classes/components/Quiz/events'
import {
  useCategoriesStatic,
  useSelectedCategories,
  useAiCategoryLabel,
  AI_MOTIVATION_QUERY_PARAM,
} from 'pages/find-my-classes/hooks/useCategorySelections'

export const MotivationRefinement = () => {
  const { t } = useTranslation('@mc/next')
  const { trackOptionSelected, trackMotivationRefinementComplete } =
    useQuizEvents()

  const categories = useCategoriesStatic()

  useTrackPen7367Experiment()

  // Read AI category checkbox state from XState context (SSR-consistent via
  // router.query in Quiz.tsx, and refreshed from the URL by categoryInterests.exit
  // when arriving from Q1). Avoids SSR/hydration mismatches that caused the
  // checkbox to intermittently render unchecked.
  const hasAiMotivationParam = QuizMachineContext.useSelector(
    (s) => s.context.hasAiMotivationParam,
  )
  const aiCategoryLabel = useAiCategoryLabel()

  const { isSelected, toggle, selectedCategories, syncToUrl } =
    useSelectedCategories()
  const [aiChecked, setAiChecked] = useState(hasAiMotivationParam)

  if (!categories) {
    reportError(
      new Error('Quiz Motivation Refinement: Categories are undefined'),
    )
    return null
  }

  const sortedCategories = [...categories].sort((a, b) =>
    a.name.localeCompare(b.name),
  )

  // Allow proceeding with only the AI category selected
  const canSubmitAnswer = selectedCategories.size > 0 || aiChecked

  const onClickNext = () => {
    const selectedOptions = [...selectedCategories.keys()]
    if (aiChecked) {
      selectedOptions.push(aiCategoryLabel)
    }

    trackMotivationRefinementComplete({
      selectedOptions,
      selectedCategories: [...selectedCategories.keys()],
      selectedCategoryIds: [...selectedCategories.values()],
    })
    // Atomically update the AI query param alongside category params
    syncToUrl({ [AI_MOTIVATION_QUERY_PARAM]: aiChecked ? 'true' : null })
  }

  const checkboxClasses = (i: number, selected: boolean) =>
    cn(
      'mc-p-6 mc-my-1 mc-text--left',
      selected ? 'mc-bg-gray-300' : 'mc-bg-gray-100',
      {
        'mc-corners-bottom-left--sm mc-corners-bottom-right--sm':
          i === sortedCategories.length - 1,
      },
    )

  return (
    <InteractiveStep
      title={t('findMyClasses.steps.motivationRefinement.title')}
    >
      <div className='mc-my-7'>
        <div className='row no-gutters-vertical justify-content-center'>
          <div className='col-12 col-md-10 col-lg-7'>
            <div className='mc-my-2'>
              {/* AI category shown above real categories, pre-selected when AI motivation was chosen */}
              <Checkbox
                className={cn(
                  'mc-p-6 mc-my-1 mc-text--left',
                  aiChecked ? 'mc-bg-gray-300' : 'mc-bg-gray-100',
                  'mc-corners-top-left--sm mc-corners-top-right--sm',
                  sortedCategories.length === 0 &&
                    'mc-corners-bottom-left--sm mc-corners-bottom-right--sm',
                )}
                checked={aiChecked}
                label={aiCategoryLabel}
                option='ai'
                onChange={() => {
                  trackOptionSelected({
                    value: aiCategoryLabel,
                    checked: !aiChecked,
                  })
                  setAiChecked((v) => !v)
                }}
                name='option-ai'
              />
              {sortedCategories.map((cat, i) => (
                <Checkbox
                  key={cat.id}
                  className={checkboxClasses(i, isSelected(cat.id))}
                  checked={isSelected(cat.id)}
                  label={cat.name}
                  option={cat.id}
                  onChange={() => {
                    trackOptionSelected({
                      value: cat.name,
                      checked: !isSelected(cat.id),
                    })
                    toggle(cat.id)
                  }}
                  name={`option-${cat.id}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <ButtonsSection
        onNext={onClickNext}
        disabled={!canSubmitAnswer}
        disabledText={t('findMyClasses.subcatInterests.noneSelected')}
      />
    </InteractiveStep>
  )
}
